import React, { useState } from "react";
import { Upload, Download, X, CheckCircle, AlertTriangle } from "lucide-react";
import api from "../api";
import { Bt } from "../components";
import { notify } from "../notify";

// Salary data is for a completed month, so the picker defaults to last month.
const lastMonth = () => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
// Same columns, in the same order, as the HR salary sheet, so a filled sample
// uploads as-is. "Email id" is the column used to find the employee.
const SAMPLE_HEADERS = ["S.No", "Name", "Employe Code", "Center", "Gender", "Present Working Days", "Gross Salary", "LPA", "Actual Gross Salary by Day", "Basic & DA", "HRA", "Punctulity Allowance", "Special Allowance", "Fixed Allowance", "Travel Allowance", "Arrear", "Reimbusment", "BYOD", "Work Deduction", "Loan EMI", "PF", "TDS", "GHI", "Total Addition", "Total Deduction", "Net Salary", "Bank Account ", "IFSC Code ", "Email id", "Date of Joining"];
const SAMPLE_ROWS = [
  [1, "Ravi Sharma", "ZLPUN00001", "Pune", "Male", 30, 30000, 360000, 30000, 15000, 7500, 3000, 2250, 2250, 0, 0, 0, 700, 0, 0, 3600, 0, 250, 3850, 3850, 26850, "123456789012", "HDFC0001234", "ravi.sharma@zucol.in", "2022-01-07"],
  [2, "Priya Patel", "ZLPUN00002", "Pune", "Female", 29, 22000, 264000, 21266.67, 11000, 5500, 2200, 1650, 1650, 0, 0, 0, 0, 0, 0, 2640, 0, 170, 2810, 2810, 19190, "987654321098", "KKBK0000958", "priya.patel@zucol.in", "2023-04-03"],
];
const downloadSample = () => {
  const q = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const csv = [SAMPLE_HEADERS, ...SAMPLE_ROWS].map(r => r.map(q).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a"); a.href = url; a.download = "salary_upload_sample.csv";
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
};
const money = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN"));

// Two-step upload: the first call is a dry run that shows which rows match an
// employee by email; nothing is written until the user confirms.
export const BulkSalaryUploadModal = ({ onClose }) => {
  const [file, setFile] = useState(null);
  const [period, setPeriod] = useState(lastMonth());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(false);

  const send = async (dryRun, f = file, p = period) => {
    const fd = new FormData();
    fd.append("file", f);
    return api.upload(`/api/employees/bulk-salary-upload?period=${p}${dryRun ? "&dryRun=1" : ""}`, fd);
  };
  // The file is checked the moment it is chosen (or the month changes), so any
  // problem shows up here, before anything is saved.
  const check = async (f = file, p = period) => {
    if (!f || !p) return;
    setBusy(true); setErr(""); setPreview(null); setDone(false);
    try { setPreview(await send(true, f, p)); } catch (ex) { setErr(ex.message || "Could not read the file"); }
    setBusy(false);
  };
  const onFile = (e) => { const f = e.target.files?.[0] || null; setFile(f); e.target.value = ""; check(f, period); };
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const r = await send(false);
      setPreview(r); setDone(true);
      notify(`Salary data saved for ${r.matched_count} employee${r.matched_count === 1 ? "" : "s"}`, { title: "Saved", variant: "success" });
    } catch (ex) { setErr(ex.message || "Upload failed"); }
    setBusy(false);
  };

  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[936px] max-h-[85vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex justify-between mb-4"><span className="text-base font-extrabold text-t0">Bulk Salary Upload</span><button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>

      <div className="p-3 bg-bg2 border border-b0 rounded-[10px] mb-[10px]">
        <div className="text-[14px] font-bold text-t0 mb-1">Upload the HRMS Salary Data sheet (.xlsx or .csv)</div>
        <div className="text-[12px] text-t3 mb-3">Each row is matched to an employee by <b>Email id</b>. For every match we store the CTC (LPA), bank account + IFSC, the salary breakdown, and the whole row — present working days, arrear, reimbursement, BYOD, work deduction, loan EMI, PF, TDS, GHI, totals and net salary — against that employee for the month below. Re-uploading a month replaces it. Rows without a matching email are skipped and listed.</div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[12px] text-t3 uppercase mb-[3px]">Salary month</div>
            <input type="month" value={period} max={new Date().toISOString().slice(0, 7)} onChange={e => { setPeriod(e.target.value); check(file, e.target.value); }} className="px-3 py-[7px] rounded-[10px] bg-bg1 border border-b2 text-[14px] text-t0" />
          </div>
          <label className="inline-flex items-center gap-[6px] px-[14px] py-[8px] rounded-[10px] bg-transparent text-t1 border border-b2 text-[14px] font-semibold cursor-pointer">
            <Upload size={12} />{file?.name || "Choose file"}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden" />
          </label>
          <Bt v="secondary" icon={Download} onClick={downloadSample}>Download sample</Bt>
          <Bt onClick={() => check()} disabled={!file || !period || busy}>{busy && !preview ? "Checking…" : "Check again"}</Bt>
        </div>
      </div>

      {err && <div className="p-3 bg-rb border border-rd rounded-[10px] mb-[10px] text-[14px] text-r">{err}</div>}

      {preview && <>
        <div className="p-3 bg-bg2 border border-b0 rounded-[10px] mb-[10px]">
          <div className="text-[14px] font-bold text-t0 mb-2">
            {done ? "Saved" : "Ready to save"} — {preview.matched_count} OK, {preview.invalid_count} with errors, {preview.unmatched_count} not matched (of {preview.total_rows} rows) for {preview.period}
          </div>
          {preview.ctc_skipped && <div className="text-[12px] text-amber-600 mb-2">You don't have permission to change CTC, so the LPA column was not applied. Everything else was.</div>}
          {preview.matched_count > 0 && <div className="max-h-[220px] overflow-auto border border-b0 rounded-lg bg-bg1">
            <table className="w-full border-collapse text-[14px]">
              <thead><tr className="bg-bg2">{["Row", "Employee", "Email", "Present days", "Gross", "LPA", "Net", ""].map(h => <th key={h} className="px-2 py-[6px] text-left text-[12px] text-t3 border-b border-b0">{h}</th>)}</tr></thead>
              <tbody>{preview.matched.map(m => <tr key={m.row} className="border-b border-b0">
                <td className="px-2 py-[5px] text-t3">{m.row}</td>
                <td className="px-2 py-[5px] text-t0">{m.name} <span className="text-t3 font-mono text-[12px]">{m.employee_id}</span></td>
                <td className="px-2 py-[5px] text-t1">{m.email}</td>
                <td className="px-2 py-[5px] text-t1">{m.present_days ?? "—"}</td>
                <td className="px-2 py-[5px] text-t1">{money(m.gross_salary)}</td>
                <td className="px-2 py-[5px] text-t1">{money(m.lpa)}</td>
                <td className="px-2 py-[5px] text-t1">{money(m.net_salary)}</td>
                <td className="px-2 py-[5px] text-[12px] text-amber-600">{m.warnings.length > 0 && <span title={m.warnings.join("\n")} className="inline-flex items-center gap-1"><AlertTriangle size={12} />{m.warnings.join("; ")}</span>}</td>
              </tr>)}</tbody>
            </table>
          </div>}
        </div>
        {preview.invalid_count > 0 && <div className="p-3 bg-rb border border-rd rounded-[10px] mb-[10px]">
          <div className="text-[14px] font-bold text-r mb-1">{preview.invalid_count} row{preview.invalid_count === 1 ? "" : "s"} with errors — {done ? "not saved" : "will NOT be saved until fixed"}</div>
          <div className="text-[12px] text-t2 mb-2">Fix these in your file and choose it again.</div>
          <div className="max-h-[180px] overflow-auto text-[13px] text-t1 bg-bg1 border border-rd rounded p-2">
            {preview.invalid.map((u, i) => <div key={i} className="mb-1"><b>Row {u.row}</b>{u.name ? ` · ${u.name}` : ""}{u.email ? ` · ${u.email}` : ""}<ul className="list-disc ml-5 text-r">{u.errors.map((m, j) => <li key={j}>{m}</li>)}</ul></div>)}
          </div>
        </div>}
        {preview.unmatched_count > 0 && <div className="p-3 bg-rb border border-rd rounded-[10px] mb-[10px]">
          <div className="text-[14px] font-bold text-r mb-1">Skipped rows ({preview.unmatched_count})</div>
          <div className="max-h-[140px] overflow-auto text-[13px] text-t1 bg-bg1 border border-rd rounded p-2">
            {preview.unmatched.map((u, i) => <div key={i}>Row {u.row}{u.name ? ` · ${u.name}` : ""}{u.email ? ` · ${u.email}` : ""} — {u.reason}</div>)}
          </div>
        </div>}
        {!done && preview.matched_count > 0 && <div className="flex justify-end gap-2">
          <Bt v="secondary" onClick={onClose}>Cancel</Bt>
          <Bt icon={CheckCircle} onClick={save} disabled={busy}>{busy ? "Saving…" : `Save for ${preview.matched_count} employee${preview.matched_count === 1 ? "" : "s"}`}</Bt>
        </div>}
        {done && <div className="flex justify-end"><Bt onClick={onClose}>Close</Bt></div>}
      </>}
    </div>
  </div>;
};
